import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { formatDistanceToNowStrict } from 'date-fns';
import { useAtomValue } from 'jotai';
import { InboxMessageData } from '@/../../server/types/apiTypes';
import { InboxEntityType, InboxMessageType } from '@/../../shared/types/inboxTypes';
import defaultUserProfilePic from '@/assets/profileAnonPicInverse.png';
import { PYMKInboxMessageAtom } from '@/store/inboxMessageAtom';
import { enShort } from '@/utils/format';

interface InboxItemProps extends InboxMessageData {
  isNew?: boolean;
  onClick?: () => void;
}

const InboxItem = ({ type, data, entityType, entityId, updatedAt, isNew = false, onClick }: InboxItemProps) => {
  const { title = '', subtitle = '', thumbnailUrl } = data ?? {};
  const randomPYMKs = useAtomValue(PYMKInboxMessageAtom);
  const navigate = useNavigate({
    from: '/inbox',
  });

  const distance = formatDistanceToNowStrict(updatedAt, {
    locale: enShort,
  });

  const [uniqId, setUniqId] = useState<string>();
  useEffect(() => {
    async function getUniqId() {
      if (entityType === InboxEntityType.CONNECTION) {
        const userRes = await axios.post('/api/getAskeeUser', { userId: entityId });
        setUniqId(userRes.data.user.uniqId);
      }
    }
    getUniqId();
  }, [entityId, entityType]);

  const handleClick = useCallback(() => {
    onClick?.();
    switch (entityType) {
      case InboxEntityType.PEOPLE_YOU_MAY_KNOW:
        navigate({
          to: '/connections/peopleYouMayKnow',
        });
        break;
      case InboxEntityType.CONNECTION:
        if (uniqId) {
          navigate({
            to: '/profile/$uniqId',
            params: {
              uniqId,
            },
          });
        }
        break;
      case InboxEntityType.ASK:
        navigate({
          to: '/ask/$askId',
          params: {
            askId: entityId,
          },
        });
        break;
      case InboxEntityType.NOTE:
        navigate({
          to: '/notes/$noteId',
          params: {
            noteId: entityId,
          },
        });
        break;
      case InboxEntityType.MULTI_ASK:
        navigate({
          to: '/ask-your-friends/$multiAskId',
          params: {
            multiAskId: entityId,
          },
        });
        break;
      default:
        break;
    }
  }, [navigate, entityType, uniqId, entityId]);

  if (type === InboxMessageType.PEOPLE_YOU_MAY_KNOW) {
    if (randomPYMKs.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-row items-center gap-4 w-full py-2 px-6 cursor-pointer" onClick={handleClick}>
        <img
          className="flex-none rounded-full object-cover w-[40px]"
          // chakra has set height to auto, so we need to set it manually
          style={{
            height: '40px',
          }}
          src={randomPYMKs[0].profilePic ?? defaultUserProfilePic}
          alt={randomPYMKs[0].displayName}
        />
        <div className="flex flex-col items-start flex-auto min-w-0">
          <div className="text-[10px] text-white w-full truncate text-left">
            People you may know on Bond: <span className="text-[#A6A6A6]">{distance}</span>
          </div>
          <div className="text-sm text-white truncate w-full text-left">
            <span className="font-bold">{randomPYMKs[0].displayName}</span>
            {randomPYMKs.length > 1 && (
              <>
                {' '}
                and <span className="font-bold">{randomPYMKs[1].displayName}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex-none text-white text-right">
          <ChevronRightIcon boxSize={6} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-row items-center gap-4 w-full py-2 px-6 cursor-pointer ${isNew ? 'bg-[#cbcbcb4d]' : ''}`}
      onClick={handleClick}
    >
      <img
        className="flex-none rounded-full object-cover w-[40px]"
        // chakra has set height to auto, so we need to set it manually
        style={{
          height: '40px',
        }}
        src={thumbnailUrl ?? defaultUserProfilePic}
        alt={title}
      />
      <div className="flex flex-col items-start flex-auto min-w-0">
        <div className="text-[10px] text-white w-full truncate text-left">
          {subtitle} <span className="text-[#A6A6A6]">{distance}</span>
        </div>
        <div className="text-sm font-bold text-white truncate w-full text-left">{title}</div>
      </div>
      <div className="flex-none text-white text-right">
        <ChevronRightIcon boxSize={6} />
      </div>
    </div>
  );
};

export default InboxItem;
